import { Typography } from 'antd';
import { useCallback, useMemo } from 'react';
import { Individual } from 'store/individuals/individual';
import useDebounce from 'utils/useDebounce';
import useIndividuals from 'hooks/useIndividuals';
import FormSelect, { SelectOption } from '../formSelect/FormSelect';
import { Cell, EmailCell, GridRow, Header } from './styles';

type Props = {
    individualId?: string; // selected Individual id
    onChange?: (id?: string) => void;
    onClear?: () => void;
    filterIndividuals?: (individual: Individual) => boolean;
};

const normalize = (v?: string | null) => (v ?? '').toString().trim();
const getName = (ind: Individual) => normalize(ind.fullName ?? (ind as any).name);
const getEmail = (ind: Individual) => normalize(ind.email);

const IndividualSelect = ({ individualId, onChange, onClear, filterIndividuals }: Props) => {
    const { entities, getIndividual, individuals, listIndividuals } = useIndividuals();

    const fetchIndividuals = useCallback(
        async (query?: string) => {
            await listIndividuals(query ? { query, size: 200 } : { size: 200 });
        },
        [listIndividuals]
    );

    const debouncedSearchIndividuals = useDebounce((q: string) => {
        fetchIndividuals(q?.trim() || undefined);
    }, 500);

    const options: SelectOption[] = useMemo(() => {
        const list = individuals.list;
        const filtered = filterIndividuals ? list.filter(filterIndividuals) : list;

        return filtered.map((ind) => {
            const name = getName(ind);
            const email = getEmail(ind);
            return { id: ind.id, value: name || email };
        });
    }, [filterIndividuals, individuals.list]);

    const displayValue = useMemo(() => {
        if (!individualId) return undefined;
        const individual = entities[individualId];

        if (!individual) {
            getIndividual(individualId);
        }
        return individual?.fullName ?? individual?.email;
    }, [entities, getIndividual, individualId]);

    return (
        <FormSelect
            hideKeys
            filterOption={false} // Use only BE filtering
            placeholder="Select user"
            value={displayValue}
            selectOptions={options}
            onSearch={debouncedSearchIndividuals}
            onChange={(id) => {
                onChange?.(id);
                listIndividuals({ size: 200 });
            }}
            onClear={() => {
                listIndividuals({ size: 200 });
                onClear?.();
            }}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 'max-content' }}
            dropdownRender={(menu) => (
                <>
                    <Header>
                        <GridRow>
                            <Cell>Name</Cell>
                            <Cell>Email</Cell>
                        </GridRow>
                    </Header>
                    {menu}
                </>
            )}
            renderOptionContent={(item) => {
                const ind = entities[item.id];
                if (!ind) return item.value;

                return (
                    <GridRow>
                        <Cell>
                            <Typography>{getName(ind)}</Typography>
                        </Cell>
                        <EmailCell>
                            <Typography>{getEmail(ind)}</Typography>
                        </EmailCell>
                    </GridRow>
                );
            }}
        />
    );
};

export default IndividualSelect;
